import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RequestStatus } from '../../utils/Enums';
import { apiClient } from '../../utils/ApiClient';
import Extension from '../../models/Extension';
import {NewExtensionFormProps} from "../../components/extensions/NewExtensionScreen";
import {ModifyExtensionFormProps} from "../../components/extensions/ModifyExtensionScreen";


interface ExtensionsState {
    extensions: Extension[];
    currentExtension?: Extension | null;
    getExtensionsStatus: RequestStatus;
    getCurrentExtension: RequestStatus;
    createExtensionStatus: RequestStatus;
    updateExtensionStatus: RequestStatus;
    deleteExtensionStatus: RequestStatus;
    errors: string[];
}

const initialState: ExtensionsState = {
    extensions: [],
    currentExtension: null,
    getExtensionsStatus: RequestStatus.Idle,
    getCurrentExtension: RequestStatus.Idle,
    createExtensionStatus: RequestStatus.Idle,
    updateExtensionStatus: RequestStatus.Idle,
    deleteExtensionStatus: RequestStatus.Idle,
    errors: [],
};

export const getExtensions = createAsyncThunk(
    'extensions/getExtensions',
    async (_, { rejectWithValue }) => {
        const response = await apiClient.get('extensions');
        return response.data;
    }
);

export const createExtension = createAsyncThunk(
    'extensions/createExtension',
    async (data: NewExtensionFormProps, { rejectWithValue }) => {
        const response = await apiClient.post('extensions', data);
        return response.data;
    }
);

export const getCurrentExtension = createAsyncThunk(
    'extensions/getCurrentExtension',
    async ({ id }: { id: string | undefined }, { rejectWithValue }) => {
        const response = await apiClient.get(`extensions/${id}`);
        return response.data;
    }
);

export const updateExtension = createAsyncThunk(
    'extensions/updateExtension',
    async (
        { id, data }: { id: string | undefined; data: ModifyExtensionFormProps },
        { rejectWithValue }
    ) => {
        const response = await apiClient.put(`extensions/${id}`, data);
        return response.data;
    }
);

export const deleteExtension = createAsyncThunk(
    'extensions/deleteExtension',
    async ({ id }: { id: string | undefined }, { rejectWithValue }) => {
        const response = await apiClient.delete(`extensions/${id}`);
        return response.data;
    }
);

const extensionsSlice = createSlice({
    name: 'extensions',
    initialState,
    reducers: {
        resetCreateExtensionStatus: (state) => {
            state.createExtensionStatus = RequestStatus.Idle;
            state.currentExtension = null;
        },
        resetUpdateExtensionStatus: (state) => {
            state.updateExtensionStatus = RequestStatus.Idle;
            state.currentExtension = null;
        },
        resetDeleteExtensionStatus: (state) => {
            state.deleteExtensionStatus = RequestStatus.Idle;
            state.currentExtension = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getExtensions.pending, (state, action) => {
                state.getExtensionsStatus = RequestStatus.Loading;
            })
            .addCase(getExtensions.fulfilled, (state, action) => {
                state.getExtensionsStatus = RequestStatus.Success;
                state.extensions = action.payload;
            })
            .addCase(getExtensions.rejected, (state, action) => {
                state.getExtensionsStatus = RequestStatus.Failed;
            })

            .addCase(createExtension.pending, (state, action) => {
                state.createExtensionStatus = RequestStatus.Loading;
                state.errors = [];
            })
            .addCase(createExtension.fulfilled, (state, action) => {
                state.createExtensionStatus = RequestStatus.Success;
            })
            .addCase(createExtension.rejected, (state, action) => {
                state.createExtensionStatus = RequestStatus.Failed;
                const errors = JSON.parse(action?.error?.message ?? '{}');
                if (errors) {
                    state.errors = Array.isArray(errors.errors) ? errors.errors : [];
                } else {
                    state.errors = ['Tuvastamata viga.'];
                }
            })

            .addCase(updateExtension.pending, (state, action) => {
                state.updateExtensionStatus = RequestStatus.Loading;
                state.errors = [];
            })
            .addCase(updateExtension.fulfilled, (state, action) => {
                state.updateExtensionStatus = RequestStatus.Success;
            })
            .addCase(updateExtension.rejected, (state, action) => {
                state.updateExtensionStatus = RequestStatus.Failed;
                const errors = JSON.parse(action?.error?.message ?? '{}');
                if (errors) {
                    state.errors = Array.isArray(errors.errors) ? errors.errors : [];
                } else {
                    state.errors = ['Tuvastamata viga.'];
                }
            })

            .addCase(deleteExtension.pending, (state, action) => {
                state.deleteExtensionStatus = RequestStatus.Loading;
            })
            .addCase(deleteExtension.fulfilled, (state, action) => {
                state.deleteExtensionStatus = RequestStatus.Success;
            })
            .addCase(deleteExtension.rejected, (state, action) => {
                state.deleteExtensionStatus = RequestStatus.Failed;
            })

            .addCase(getCurrentExtension.pending, (state, action) => {
                state.getCurrentExtension = RequestStatus.Loading;
            })
            .addCase(getCurrentExtension.fulfilled, (state, action) => {
                state.getCurrentExtension = RequestStatus.Success;
                state.currentExtension = action.payload;
            })
            .addCase(getCurrentExtension.rejected, (state, action) => {
                state.getCurrentExtension = RequestStatus.Failed;
            });
    },
});

export const {
    resetCreateExtensionStatus,
    resetUpdateExtensionStatus,
    resetDeleteExtensionStatus,
} = extensionsSlice.actions;
export default extensionsSlice.reducer;